// react
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { faClose } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Badge from "../../../../../../components/Badge";
import Button from "../../../../../../components/Button";
import EmployeePicker from "../../../../../../components/EmployeePicker";
// components
import DateInput from "../../../../../../components/Inputs/DatePicker";
import SelectInput from "../../../../../../components/Inputs/SelectInput";
import Checkboxes from "../../../../../../components/Inputs/SelectionOptions/Checkboxes";
import { convertStringArrayToUsableOptionsForCheckbox } from "../../../../../../components/Inputs/SelectionOptions/Checkboxes/LegacyCheckboxUtilities";
import { RadioButtons } from "../../../../../../components/Inputs/SelectionOptions/RadioButtons";
import OutlookPicker from "../../../../../../components/OutlookPicker";
import PageBanner from "../../../../../../components/PageBanner";
import Panel from "../../../../../../components/Panel";
// components
import Spacer from "../../../../../../components/Spacer";
import StrategyCategoryPicker from "../../../../../../components/StrategyCategoryPicker";
import Typography from "../../../../../../components/Text";
import useQueryBuilderStore from "../../../../components/QueryBuilder/store";
import AdditionalFilters from "./AdditionalFilters";
// config
import Config from "./Config";

// schemas
import { FilterOptions, SelectedFilter } from "../../../../schemas/report";

// store
import useReportStore from "../../store";

import { useRbacPrimaryRolePermissions } from "../../../../../../../react/api/hooks/defs/rbac";
import { useFeatureFlagQuery, useRoleFieldEnabled } from "../../../../../../hooks/useFeatureFlag";

// selectors
import { getCompanySettingsContext } from "../../../../../../selectors/settings";

import { getReportTypeSpecificPermissions } from "../../utils";
// utils
import { getCanSetFilter } from "./utils";

import { reportTypeLookup } from "../../../../config";
// styles
import css from "./Filters.module.scss";

interface DefaultQuickFilter {
	id: string;
	label: string;
	type: string;
	info?: string;
}

interface BaseProps {
	selectedFilter: SelectedFilter[];
	filterOptions: FilterOptions;
	setSelectedFilter: (selectedFilter: SelectedFilter[]) => void;
	onClose: () => void;
	applyFilterDataSet: () => void;
	isOpen: boolean;
}

const Filters = (props: BaseProps) => {
	// selectedFilter holds the applied filters
	const { selectedFilter, setSelectedFilter, onClose, applyFilterDataSet, filterOptions, isOpen } = props;
	const { data: permissions } = useRbacPrimaryRolePermissions("report_analytics");
	const { permissionToCreateReviewReport, permissionToCreateEmployeeReport, permissionToCreateGoalReport } =
		getReportTypeSpecificPermissions(permissions);
	const [setAdditionalFilters, report, reportCreatedByNonAdmin, readOnly] = useReportStore((state) => [
		state.setAdditionalFilters,
		state.report,
		state.reportCreatedByNonAdmin,
		state.readOnly,
	]);

	const userHasAdminPermissions =
		permissionToCreateReviewReport ||
		(permissionToCreateEmployeeReport && report?.type === "employeeReport") ||
		(permissionToCreateGoalReport && report?.type === "goalReport");

	const [filterUpdate, setFilterUpdate, filterUpdateHasChanges] = useQueryBuilderStore((state) => [
		state.filterUpdate,
		state.setFilterUpdate,
		state.filterUpdateHasChanges,
	]);

	const defaultQuickFilters = report?.type
		? (Config.defaultQuickFilters[report.type] as DefaultQuickFilter[]) || []
		: [];

	// manage the extended panel state
	const [isExtendedPanelOpen, setIsExtendedPanelOpen] = useState<boolean>(false);
	const closePanel = () => {
		setIsExtendedPanelOpen(false);
	};

	useEffect(() => {
		if (isOpen) {
			// disable background scrolling on document body
			document.body.classList.add("modal-open");
		} else {
			document.body.classList.remove("modal-open");
		}
		return () => {
			document.body.classList.remove("modal-open");
		};
	}, [isOpen]);

	const formattedQuickFilters = defaultQuickFilters.reduce((acc, filter) => {
		const foundSelectedFilter = selectedFilter.find((sf) => sf.type === filter.id);

		if (foundSelectedFilter) {
			// Keep existing behavior for saved filters
			// For radio buttons, keep the full { value, label } object; for others, extract the inner value
			const filterValue =
				filter.type === "radio" || filter.type === "surveyResponseStatus"
					? foundSelectedFilter.value
					: foundSelectedFilter.value?.value || foundSelectedFilter.value;
			// For checkbox filters, an empty array is a valid explicit value (all unchecked)
			// Don't fall through to defaults in this case
			acc[filter.id] = filterValue;
		} else if (report?.type === "surveyReport" && ["surveyStatus", "surveyResponseStatus"].includes(filter.id)) {
			// Apply defaults for survey-specific filters ONLY when no saved filter exists at all
			acc[filter.id] = Config.defaultFilterValues[filter.type] ?? Config.defaultFilterValues[filter.id];
		} else {
			acc[filter.id] = null;
		}
		return acc;
	}, {});

	// stores the local state for quick filters
	const [quickFilters, setQuickFilters] = useImmer(formattedQuickFilters);
	const [canSetFilter, setCanSetFilter] = useState(true);

	// check if filter inputs are valid to enable/disable set filter button
	useEffect(() => {
		const canSet = getCanSetFilter(quickFilters, defaultQuickFilters);
		// only set state if canSet is different from current state
		if (canSet !== canSetFilter) {
			setCanSetFilter(canSet);
		}
	}, [JSON.stringify(quickFilters)]);

	const { value: roleFieldEnabled, query: roleFieldQuery } = useRoleFieldEnabled();

	const setAppliedFilters = () => {
		let filtersCopy = report?.filters ? [...report.filters] : [];
		// FF-off: saved Role filters would render as empty "Role EQUALS" shells (role labels
		// aren't fetchable) and the pipeline drops them anyway — strip them from the builder.
		if (roleFieldQuery.isSuccess && !roleFieldEnabled) {
			filtersCopy = filtersCopy
				.map((filterGroup) => filterGroup.filter((filter) => filter.type !== "role"))
				.filter((filterGroup) => filterGroup.length > 0);
		}
		setFilterUpdate(filtersCopy);
		setQuickFilters(formattedQuickFilters);
	};

	// Apply the initial additional-filters state exactly once. Gate on isPending, not isFetching: the
	// shared ["feature-flags"] key (2s staleTime) background-refetches on most mounts, and isFetching
	// would defer this sync a network RTT — long enough for the module-scope QueryBuilder store to keep
	// showing the prior report's filters. isPending is false the moment cached data exists (synchronous
	// warm-cache path); a cold cache still waits, as the strip needs the real flag value. The ref locks
	// after one apply so a later refetch can't reset filterUpdate and wipe in-flight edits. On query
	// error the strip is skipped (setAppliedFilters' isSuccess guard) — same as pre-gate behavior.
	const hasAppliedInitialFilters = useRef(false);
	useEffect(() => {
		if (hasAppliedInitialFilters.current || roleFieldQuery.isPending) return;
		hasAppliedInitialFilters.current = true;
		setAppliedFilters();
	}, [roleFieldQuery.isPending]);

	// company settings
	const { value: groupsFeatureVisible } = useFeatureFlagQuery("ff-is-groups-enabled", false);
	const groupsEnabled =
		useSelector(getCompanySettingsContext).getIn(["groups", "state"], "disabled") === "enabled" &&
		groupsFeatureVisible;

	// if the date of the first input is greater than the second input,
	// this automatically switches the inputs to be in the correct order
	const handleDateOrder = (filterID) => {
		const { start, end } = quickFilters[filterID];
		const sorted = [start, end].sort((a, b) => a - b);

		return {
			start: sorted[0],
			end: sorted[1],
		};
	};

	// gets the filter conditions to send to server depending on filter type
	const handleSetFilters = async () => {
		const allFilters = defaultQuickFilters
			.map((filter) => {
				if (filter.type === "dateRange") {
					// check date has both end and start dates
					const dateSelected = quickFilters[filter.id]?.start && quickFilters[filter.id]?.end;
					if (dateSelected) {
						const dateValue = handleDateOrder(filter.id);
						return {
							type: filter.id,
							value: dateValue,
						};
					}
					return null;
				}
				if (filter.type === "radio") {
					// For radio buttons, skip if "all" is selected (no filter applied)
					const radioValue = quickFilters[filter.id];
					if (radioValue && radioValue.value !== "all") {
						return {
							type: filter.id,
							value: radioValue,
						};
					}
					return null;
				}
				// For checkbox filters (like surveyStatus), always include them even if empty
				// This distinguishes "no filter set" from "explicitly all unchecked"
				if (["checkbox", "surveyStatus"].includes(filter.type)) {
					const checkboxValue = quickFilters[filter.id];
					if (Array.isArray(checkboxValue)) {
						return {
							type: filter.id,
							value: checkboxValue,
						};
					}
				}
				if (quickFilters[filter.id]) {
					return {
						type: filter.id,
						value: quickFilters[filter.id],
					};
				}
				return null;
			})
			.filter((filter) => filter !== null) as SelectedFilter[];

		// Important! this needs to be here so that when a non-admin is viewing reports, the "subject id" filter isnt' removed when other filters are applied
		if (reportCreatedByNonAdmin || !userHasAdminPermissions) {
			const subjectIdFilter = selectedFilter.find((filter) => filter.type === "subjectId");
			if (subjectIdFilter) {
				allFilters.push(subjectIdFilter);
			}
		}

		if (filterUpdateHasChanges(report?.filters || [])) {
			setAdditionalFilters(filterUpdate);
		}

		setSelectedFilter(allFilters);
		applyFilterDataSet();

		onClose();
	};

	const handleQuickFilterChange = (filterID, value) => {
		setQuickFilters((draft) => {
			draft[filterID] = value;
		});
	};

	const clearFilter = (filterID, filterType) => {
		if (readOnly) {
			return;
		}
		setQuickFilters((draft) => {
			draft[filterID] = Config.defaultFilterValues[filterType];
		});
	};

	const clearAdditionalFilters = () => {
		if (readOnly) {
			return;
		}
		setFilterUpdate([]);
	};

	const closeFilterPanel = () => {
		// clear filters that haven't been applied
		setAppliedFilters();
		onClose();
		setIsExtendedPanelOpen(false);
	};

	const getFilterValue = (filterID, filterType) => {
		return quickFilters[filterID] ? quickFilters[filterID] : Config.defaultFilterValues[filterType];
	};
	let dataIsFilteredMessage = "";

	if (!userHasAdminPermissions && !reportCreatedByNonAdmin) {
		dataIsFilteredMessage =
			"Report data is view-only and has been filtered based on your user permissions and data access";
	} else if (userHasAdminPermissions && reportCreatedByNonAdmin) {
		dataIsFilteredMessage =
			"Report data is view-only and has been filtered based on the report creator's permissions and data access";
	} else if (!userHasAdminPermissions && reportCreatedByNonAdmin) {
		dataIsFilteredMessage = "Report data has been filtered based on your user permissions and data access";
	}

	return (
		<Panel
			extendedPanelOpen={isExtendedPanelOpen}
			extendedPanelContent={<AdditionalFilters closePanel={closePanel} />}
			isOpen={isOpen}
			onClose={closeFilterPanel}
		>
			<div data-testid="quick-filters" className={css.scrollContainer}>
				<div className={css.content}>
					<div className={css.filtersHead}>
						<div className={css.filterHeadLabel}>{`${
							reportTypeLookup[report?.type || ""] || ""
						} Filters`}</div>

						<div className={css.closeButton} onClick={closeFilterPanel}>
							<div className={css.iconWrapper}>
								<FontAwesomeIcon icon={faClose} />
							</div>
						</div>
					</div>
					<div className={css.filtersContainer}>
						{(!userHasAdminPermissions || reportCreatedByNonAdmin) && (
							<div className={css.filterBanner}>
								<PageBanner type="info" isDismissible={false} title={dataIsFilteredMessage} />
							</div>
						)}
						{defaultQuickFilters.map((filter) => {
							const filterValue = getFilterValue(filter.id, filter.type);
							const filterEnabled = filter.id === "department" ? groupsEnabled : true;
							return (
								<div className={`${css.filterItem} ${css.select}`} key={filter.id}>
									<div
										className={
											!readOnly
												? css.filterContent
												: `${css.filterContent} ${css.disabledForManagers}`
										}
									>
										{filter.type === "date" ? (
											<div>
												<DateInput
													label={filter.label}
													info={filter.info ? { message: filter.info } : undefined}
													hasIcon={true}
													autoWidth={true}
													value={filterValue}
													onChange={(value) => handleQuickFilterChange(filter.id, value)}
													disabled={readOnly}
													textInputClassName={readOnly ? css.disabledForManagers : ""}
												/>
												<div
													onClick={() => clearFilter(filter.id, filter.type)}
													className={
														!readOnly
															? css.clearButton
															: `${css.clearButton} ${css.disabled}`
													}
												>
													Clear
												</div>
											</div>
										) : null}
										{filter.type === "dateRange" ? (
											<div>
												<Typography weight="semi-bold" size="paragraph-3">
													{filter.label}
												</Typography>
												<Spacer height={1} />
												<DateInput
													hasIcon={true}
													autoWidth={true}
													value={filterValue?.start}
													onChange={(value) =>
														handleQuickFilterChange(filter.id, {
															...(filterValue || {}),
															start: value,
														})
													}
													disabled={readOnly}
													textInputClassName={readOnly ? css.disabledForManagers : ""}
												/>
												<Spacer height={1} />
												<DateInput
													hasIcon={true}
													autoWidth={true}
													value={filterValue?.end}
													onChange={(value) =>
														handleQuickFilterChange(filter.id, {
															...(filterValue || {}),
															end: value,
														})
													}
													disabled={readOnly}
													textInputClassName={readOnly ? css.disabledForManagers : ""}
												/>
												<div
													onClick={() => clearFilter(filter.id, filter.type)}
													className={
														!readOnly
															? css.clearButton
															: `${css.clearButton} ${css.disabled}`
													}
												>
													Clear
												</div>
											</div>
										) : null}
										{filter.type === "multiSelect" && filterEnabled ? (
											<div>
												<SelectInput
													label={filter.label}
													isMultiSelect={true}
													isSearchable={true}
													cacheOptions
													results={filterOptions[filter.id] || []}
													onChange={(value) => {
														handleQuickFilterChange(filter.id, value);
													}}
													// Don't explicitly set the label since it is not static
													value={filterValue}
													// max of 10 selections
													isOptionDisabled={() =>
														quickFilters[filter.id]?.length > 9 ? true : false
													}
													isClearable={false}
													disabled={readOnly}
												/>
											</div>
										) : null}
										{["checkbox", "surveyStatus"].includes(filter.type) ? (
											<div>
												<Checkboxes
													label={filter.label}
													options={Config.checkBoxOptions[filter.id]}
													value={convertStringArrayToUsableOptionsForCheckbox(filterValue)}
													onChange={(values) => {
														const formattedValues = values.map((value) => value.value);
														handleQuickFilterChange(filter.id, formattedValues);
													}}
													disabled={
														readOnly
															? Config.checkBoxOptions[filter.id].map(
																	(option) => option.value
																)
															: []
													}
												/>
											</div>
										) : null}
										{filter.type === "surveyResponseStatus" ? (
											<div>
												<RadioButtons
													label={filter.label}
													options={Config.radioOptions[filter.id]}
													value={filterValue}
													onChange={(value) => handleQuickFilterChange(filter.id, value)}
												/>
											</div>
										) : null}
										{filter.type === "person" ? (
											<div>
												<EmployeePicker
													isMultiSelect={true}
													label={filter.label}
													value={filterValue || []}
													onChange={(value) => handleQuickFilterChange(filter.id, value)}
												/>
											</div>
										) : null}
										{filter.type === "goalCategory" ? (
											<div>
												<StrategyCategoryPicker
													isMultiSelect={true}
													label={filter.label}
													value={filterValue || []}
													onChange={(value) => handleQuickFilterChange(filter.id, value)}
												/>
											</div>
										) : null}
										{filter.type === "goalOutlook" ? (
											<OutlookPicker
												multiSelect={true}
												label={filter.label}
												value={filterValue || []}
												onChange={(value) => handleQuickFilterChange(filter.id, value)}
											/>
										) : null}
									</div>
								</div>
							);
						})}
						{/* Additional custom filters toggle with display of count */}
						<div className={`${css.filterItem} ${css.select}`}>
							<div className={css.filterContent}>
								<Typography weight="semi-bold" size="paragraph-2">
									Additional Filters
								</Typography>
								<div className={css.additionalFiltersArea}>
									<Badge
										title={`${filterUpdate.length} additional filter${
											filterUpdate.length !== 1 ? "s" : ""
										}`}
										count={filterUpdate.length}
										overflowCount={9}
									>
										<Button
											className={css.additionalFiltersButton}
											isHollow={true}
											onClick={() => setIsExtendedPanelOpen(!isExtendedPanelOpen)}
											disabled={Boolean(readOnly && !report?.filters?.length)}
										>
											<FontAwesomeIcon icon={["fas", "plus"]} />
											Add Filters
										</Button>
									</Badge>
								</div>
								<div
									data-testid="clear-additionalFilters"
									onClick={() => clearAdditionalFilters()}
									className={!readOnly ? css.clearButton : `${css.clearButton} ${css.disabled}`}
									style={readOnly ? { opacity: 0.48 } : {}}
								>
									Clear
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{!readOnly ? (
				<div className={css.setFilterContainer}>
					<div data-testid="apply-filters-button">
						<Button
							data-testid="updateButton"
							onClick={handleSetFilters}
							className={css.setFilterButton}
							disabled={!canSetFilter}
						>
							Apply Filters
						</Button>
					</div>
				</div>
			) : null}
		</Panel>
	);
};

export default Filters;
